import { type FunctionComponent, type PropsWithChildren, useContext } from 'react';
import { AlertNotificationContext, type AlertNotificationType } from '../../../contexts/alert-notification.context';
import AlertOctagonIcon from '../../svgs/untitled-ui/alert-octagon.svg';
import AlertTriangleIcon from '../../svgs/untitled-ui/alert-triangle.svg';
import CheckCircleIcon from '../../svgs/untitled-ui/check-circle.svg';
import InfoCircleIcon from '../../svgs/untitled-ui/info-circle.svg';
import LockIcon from '../../svgs/untitled-ui/lock.svg';
import XCloseIcon from '../../svgs/untitled-ui/x-close.svg';

const fontClassMap = {
	SMALL: 'f7',
	MEDIUM: 'f6',
	LARGE: 'f5',
	GLOBAL: 'f5'
};

const iconClassMap = {
	SMALL: 'f5',
	MEDIUM: 'f4',
	LARGE: 'f4',
	GLOBAL: 'f4'
};

const closeIconMarginTopClassMap = {
	SMALL: 1,
	MEDIUM: 1,
	LARGE: 3,
	GLOBAL: 3
};

const paddingClassMap = {
	SMALL: 'pa2',
	MEDIUM: 'pa2',
	LARGE: 'pa3',
	GLOBAL: 'pv3 pl3'
};

const iconPaddingClassMap = {
	SMALL: 'pr2 pt2',
	MEDIUM: 'pr2 pt2',
	LARGE: 'ph3 pt3',
	GLOBAL: 'ph3 pt3'
};

const iconPaddingTopClassMap = {
	SMALL: 7,
	MEDIUM: 8,
	LARGE: 18,
	GLOBAL: 18
};

const getClasses = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ');

const getAlertBackgroundColor = (alertType: AlertNotificationType) =>
	alertType === 'grey' ? 'bg-theme-grey-lighter' : `bg-theme-${alertType}-light`;

const getAlertTextColor = (alertType: AlertNotificationType) => `theme-${alertType === 'grey' ? 'grey-dark' : alertType}`;

const getRoundedClasses = (roundedDirection: 'top' | 'all') => ({
	container: roundedDirection === 'top' ? 'br2 br--top' : 'br2',
	icon: roundedDirection === 'top' ? '' : 'br2 br--left',
	iconTopLeftRadius: roundedDirection === 'top' ? '0.25rem' : undefined
});

export type AlertNotificationProps = {
	alertType: AlertNotificationType;
	size: 'SMALL' | 'MEDIUM' | 'LARGE' | 'GLOBAL';
	showCloseButton?: boolean;
	alignCenter?: boolean;
	onClose?: () => void;
	roundedDirection?: 'top' | 'all';
	icon?: JSX.Element;
};

const iconsMap = {
	warning: <AlertTriangleIcon data-testid="AlertTriangleIcon" />,
	danger: <AlertOctagonIcon data-testid="AlertOctagonIcon" />,
	info: <InfoCircleIcon />,
	success: <CheckCircleIcon />,
	grey: <LockIcon />
};

/**
 * The alert notification component should be used to display an alert banner above the header of the page.
 */
export const AlertNotification: FunctionComponent<PropsWithChildren<AlertNotificationProps>> = ({
	alertType,
	size,
	showCloseButton,
	alignCenter,
	children,
	onClose,
	roundedDirection = 'all',
	icon
}) => {
	const { closeNotification } = useContext(AlertNotificationContext);

	if (!children) return null;

	const closeClick = () => {
		onClose?.();
		closeNotification();
	};

	const rounded = getRoundedClasses(roundedDirection);
	const isGlobal = size === 'GLOBAL';

	return (
		<div
			className={getClasses(getAlertBackgroundColor(alertType), `ba b--theme-${alertType}`, 'flex items-stretch', rounded.container)}
			data-testid="alert-notification"
			data-automation="alert-notification"
			style={isGlobal ? { boxShadow: '0 12px 32px rgba(0, 0, 0, 0.3)' } : undefined}>
			<div
				className={getClasses(
					'theme-white',
					rounded.icon,
					`bg-theme-${alertType}`,
					iconClassMap[size],
					'ph2',
					iconPaddingClassMap[size]
				)}
				style={{
					paddingTop: iconPaddingTopClassMap[size],
					transform: 'translateX(-1px)',
					borderTopLeftRadius: rounded.iconTopLeftRadius
				}}>
				{icon ?? iconsMap[alertType]}
			</div>

			<div
				className={getClasses(
					'pt w-100 ma0',
					alignCenter && 'tc',
					getAlertTextColor(alertType),
					paddingClassMap[size],
					'lh-copy self-start',
					fontClassMap[size]
				)}>
				{children}
			</div>
			{showCloseButton && (
				<button
					onClick={closeClick}
					type="button"
					className={getClasses(
						'close pa0 b--none bg-transparent pointer flex',
						iconPaddingClassMap[size],
						'lh-copy self-start ml5'
					)}
					data-dismiss="alert"
					aria-label="Close"
					style={{ marginTop: closeIconMarginTopClassMap[size] }}>
					<XCloseIcon className={`text-base theme-${alertType}`} data-testid="XCloseIcon" />
				</button>
			)}
		</div>
	);
};

/**
 * An alert notification that is fixed at the top of the window.
 */
export const GlobalAlertNotification: FunctionComponent = () => {
	const { message, type, onDismiss } = useContext(AlertNotificationContext);
	return message ? (
		<div className="absolute top-1 mt2-ns ph3 ph0-ns w-100 h-100 disableClickEvents">
			<div
				className="sticky top-2 z-5"
				data-testid={'global-alert-notification'}
				style={{ width: 'fit-content', margin: '0 auto', pointerEvents: 'auto' }}>
				<AlertNotification alertType={type} size={'GLOBAL'} showCloseButton={true} onClose={onDismiss}>
					{message}
				</AlertNotification>
			</div>
		</div>
	) : null;
};
